import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { Loader2, Plus, Search, Trash2, X, ChevronDown, ChevronUp } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { OptionList } from "@/utils/types";

interface ProductItem {
  id: number;
  name: string;
  menu_display_name: string;
  image: string;
  unit_price: string;
}

interface DailySpecialEntry {
  id?: number; // existing special ID
  product: number;
  productName: string;
  productImage: string;
  day_of_week: number;
  special_price: string;
  limit_per_order: number;
  name: string;
  option_lists: OptionList[];
  is_active: boolean;
  isExisting?: boolean;
}

interface ApiDailySpecial {
  id: number;
  product: number;
  day_of_week: number;
  special_price: string;
  name: string;
  limit_per_order: number;
  option_lists: OptionList[];
  is_active: boolean;
}

interface DaySpecials {
  day: number;
  day_name: string;
  specials: ApiDailySpecial[];
  count: number;
}

interface DailySpecialModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DAYS_OF_WEEK = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" },
];

export default function DailySpecialModal({
  open,
  onOpenChange,
}: DailySpecialModalProps) {
  const { t } = useTranslation();
  const { user } = useUser();
  const [axiosInstance] = useAxiosInstance();
  const [searchResults, setSearchResults] = useState<ProductItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSpecials, setSelectedSpecials] = useState<DailySpecialEntry[]>(
    [],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [allOptionLists, setAllOptionLists] = useState<OptionList[]>([]);
  const [expandedOptionLists, setExpandedOptionLists] = useState<Set<number>>(
    new Set(),
  );
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch existing daily specials when modal opens
  useEffect(() => {
    if (!open || !axiosInstance || !user?.selectedBranch) return;

    const fetchExistingSpecials = async () => {
      const branchId = user?.selectedBranch?.id;
      if (!branchId) return;

      setIsLoading(true);
      try {
        const response = await axiosInstance.get(
          `/discount/daily-special/by-day/?branch=${branchId}`,
        );

        // Collect all specials from all days
        const allSpecials: DailySpecialEntry[] = [];
        const productIds: Set<number> = new Set();

        Object.values(response.data).forEach((dayData: unknown) => {
          const day = dayData as DaySpecials;
          if (day.specials && Array.isArray(day.specials)) {
            day.specials.forEach((special: ApiDailySpecial) => {
              productIds.add(special.product);
              allSpecials.push({
                id: special.id,
                product: special.product,
                productName: `Product #${special.product}`,
                productImage: "",
                day_of_week: special.day_of_week,
                special_price: special.special_price,
                limit_per_order: special.limit_per_order || 1,
                name: special.name || "",
                option_lists: special.option_lists || [],
                is_active: special.is_active ?? true,
                isExisting: true,
              });
            });
          }
        });

        // Fetch product details for all products
        if (productIds.size > 0) {
          const apiUrl =
            import.meta.env.VITE_APP_REDBIRDPOSBE_PRODUCT_INFORMATION ?? "";
          const productPromises = Array.from(productIds).map((id) =>
            axiosInstance.get(`${apiUrl}${id}/`).catch(() => null),
          );
          const productResponses = await Promise.all(productPromises);

          const productMap = new Map<number, ProductItem>();
          productResponses.forEach((res) => {
            if (res?.data) {
              productMap.set(res.data.id, res.data);
            }
          });

          // Update specials with product details
          allSpecials.forEach((special) => {
            const product = productMap.get(special.product);
            if (product) {
              special.productName = product.menu_display_name || product.name;
              special.productImage = product.image || "";
            }
          });
        }

        setSelectedSpecials(allSpecials);
      } catch (error) {
        console.error("Error fetching existing specials:", error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchOptionLists = async () => {
      const branchId = user?.selectedBranch?.id;
      if (!branchId) return;
      try {
        const apiUrl =
          import.meta.env.VITE_APP_REDBIRDPOSBE_OPTIONLIST_INFORMATION ?? "";
        let url = `${apiUrl}?branch=${branchId}`;
        const options: OptionList[] = [];
        while (url) {
          const response = await axiosInstance.get(url);
          const { results, next } = response.data;
          options.push(...results);
          url = next;
        }
        setAllOptionLists(options);
      } catch (error) {
        console.error("Error fetching option lists:", error);
      }
    };

    fetchExistingSpecials();
    fetchOptionLists();
  }, [open, axiosInstance, user?.selectedBranch]);

  // Debounced search - 2 seconds
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      if (!axiosInstance || !user?.selectedBranch) {
        setIsSearching(false);
        return;
      }

      try {
        const apiUrl =
          import.meta.env.VITE_APP_REDBIRDPOSBE_PRODUCT_INFORMATION ?? "";
        const url = `${apiUrl}?branch=${user.selectedBranch.id}&page=1&page_size=10&menu_display_name=${encodeURIComponent(searchQuery)}`;
        const response = await axiosInstance.get(url);
        setSearchResults(response.data.results);
        setShowResults(true);
      } catch (error) {
        console.error("Error searching products:", error);
      } finally {
        setIsSearching(false);
      }
    }, 2000);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, axiosInstance, user?.selectedBranch]);

  // Reset search when modal closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSearchResults([]);
      setShowResults(false);
    }
  }, [open]);

  const addProduct = (product: ProductItem) => {
    if (selectedSpecials.some((s) => s.product === product.id)) {
      toast.error(t("back.management.menu.dailySpecial.alreadyAdded"));
      return;
    }

    setSelectedSpecials((prev) => [
      ...prev,
      {
        product: product.id,
        productName: product.menu_display_name || product.name,
        productImage: product.image,
        day_of_week: (new Date().getDay() + 6) % 7, // Convert JS Sunday=0 to Monday=0
        special_price: product.unit_price,
        limit_per_order: 1,
        name: "",
        option_lists: [],
        is_active: true,
      },
    ]);
    setSearchQuery("");
    setSearchResults([]);
    setShowResults(false);
  };

  const updateSpecialEntry = (
    productId: number,
    field: keyof DailySpecialEntry,
    value: string | number | boolean,
  ) => {
    setSelectedSpecials((prev) =>
      prev.map((s) => (s.product === productId ? { ...s, [field]: value } : s)),
    );
  };

  const removeSpecialEntry = (productId: number) => {
    setSelectedSpecials((prev) => prev.filter((s) => s.product !== productId));
  };

  const toggleOptionList = (productId: number, optionList: OptionList) => {
    setSelectedSpecials((prev) =>
      prev.map((s) => {
        if (s.product !== productId) return s;
        const exists = s.option_lists.some((ol) => ol.id === optionList.id);
        return {
          ...s,
          option_lists: exists
            ? s.option_lists.filter((ol) => ol.id !== optionList.id)
            : [...s.option_lists, optionList],
        };
      }),
    );
  };

  const removeOptionList = (productId: number, optionListId: number) => {
    setSelectedSpecials((prev) =>
      prev.map((s) =>
        s.product === productId
          ? {
              ...s,
              option_lists: s.option_lists.filter(
                (ol) => ol.id !== optionListId,
              ),
            }
          : s,
      ),
    );
  };

  const toggleExpandOptionLists = (productId: number) => {
    setExpandedOptionLists((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (
      !axiosInstance ||
      !user?.selectedBranch ||
      selectedSpecials.length === 0
    )
      return;

    setIsSubmitting(true);
    try {
      // Separate existing and new specials
      const existingSpecials = selectedSpecials.filter(
        (s) => s.isExisting && s.id,
      );
      const newSpecials = selectedSpecials.filter((s) => !s.isExisting);

      // Helper to format option lists for API payload
      const formatOptionLists = (optionLists: OptionList[]) =>
        optionLists.map((ol) => ({
          name: ol.name,
          branch: user!.selectedBranch!.id,
          options: ol.options.map((o) => o.id),
        }));

      // PATCH existing specials
      const patchPromises = existingSpecials.map((s) =>
        axiosInstance.patch(`/discount/daily-special/${s.id}/`, {
          day_of_week: s.day_of_week,
          special_price: s.special_price,
          limit_per_order: s.limit_per_order,
          name: s.name || "",
          is_active: s.is_active,
          option_lists: formatOptionLists(s.option_lists),
        }),
      );

      // Bulk create new specials
      let bulkCreatePromise: Promise<unknown> | null = null;
      if (newSpecials.length > 0) {
        const payload = {
          branch: user.selectedBranch.id,
          specials: newSpecials.map((s) => ({
            product: s.product,
            day_of_week: s.day_of_week,
            special_price: s.special_price,
            limit_per_order: s.limit_per_order,
            is_active: s.is_active,
            ...(s.name && { name: s.name }),
            option_lists: formatOptionLists(s.option_lists),
          })),
        };
        bulkCreatePromise = axiosInstance.post(
          "/discount/daily-special/bulk-create/",
          payload,
        );
      }

      // Wait for all operations
      await Promise.all([
        ...patchPromises,
        ...(bulkCreatePromise ? [bulkCreatePromise] : []),
      ]);

      toast.success(t("back.management.menu.dailySpecial.saveSuccess"));
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving daily specials:", error);
      toast.error(t("back.management.menu.dailySpecial.saveError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[600px] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle>
            {t("back.management.menu.dailySpecial.title")}
          </DialogTitle>
          <DialogDescription>
            {t("back.management.menu.dailySpecial.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4">
          {/* Product Search with Dropdown */}
          <div className="relative flex-shrink-0">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t(
                "back.management.menu.dailySpecial.searchProducts",
              )}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}

            {/* Search Results Dropdown */}
            {showResults && searchResults.length > 0 && (
              <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-background shadow-lg">
                {searchResults.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => addProduct(product)}
                    className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-muted/50"
                  >
                    {product.image && (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="h-10 w-10 rounded object-cover"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">
                        {product.menu_display_name || product.name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        ${product.unit_price}
                      </p>
                    </div>
                    <Plus className="h-4 w-4 text-muted-foreground" />
                  </button>
                ))}
              </div>
            )}

            {showResults && searchResults.length === 0 && !isSearching && (
              <div className="absolute z-10 mt-1 w-full rounded-md border bg-background p-4 text-center text-muted-foreground shadow-lg">
                {t("back.management.menu.dailySpecial.noProducts")}
              </div>
            )}
          </div>

          {/* Selected Products Area */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border p-4">
            <h3 className="mb-3 flex-shrink-0 font-medium">
              {t("back.management.menu.dailySpecial.configureSpecials")} (
              {selectedSpecials.length})
            </h3>
            <ScrollArea className="flex-1">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : selectedSpecials.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  {t("back.management.menu.dailySpecial.noSelection")}
                </div>
              ) : (
                <div className="space-y-4 pr-4">
                  {selectedSpecials.map((special, index) => (
                    <div
                      key={
                        special.isExisting
                          ? `existing-${special.id}`
                          : `new-${special.product}-${index}`
                      }
                      className={cn(
                        "space-y-3 rounded-lg border p-4",
                        !special.is_active && "opacity-60",
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {special.productImage && (
                            <img
                              src={special.productImage}
                              alt={special.productName}
                              className="h-10 w-10 rounded object-cover"
                            />
                          )}
                          <span className="font-medium">
                            {special.productName}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <Switch
                              id={`active-${special.product}`}
                              checked={special.is_active}
                              onCheckedChange={(value) =>
                                updateSpecialEntry(
                                  special.product,
                                  "is_active",
                                  value,
                                )
                              }
                            />
                            <Label
                              htmlFor={`active-${special.product}`}
                              className="text-xs text-muted-foreground"
                            >
                              {special.is_active
                                ? t("back.management.menu.dailySpecial.active")
                                : t(
                                    "back.management.menu.dailySpecial.inactive",
                                  )}
                            </Label>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeSpecialEntry(special.product)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label htmlFor={`day-${special.product}`}>
                            {t("back.management.menu.dailySpecial.dayOfWeek")}
                          </Label>
                          <Select
                            value={special.day_of_week.toString()}
                            onValueChange={(value) =>
                              updateSpecialEntry(
                                special.product,
                                "day_of_week",
                                parseInt(value),
                              )
                            }
                          >
                            <SelectTrigger id={`day-${special.product}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {DAYS_OF_WEEK.map((day) => (
                                <SelectItem
                                  key={day.value}
                                  value={day.value.toString()}
                                >
                                  {t(
                                    `back.management.menu.dailySpecial.days.${day.label.toLowerCase()}`,
                                  )}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor={`price-${special.product}`}>
                            {t(
                              "back.management.menu.dailySpecial.specialPrice",
                            )}
                          </Label>
                          <Input
                            id={`price-${special.product}`}
                            type="number"
                            step="0.01"
                            min="0"
                            value={special.special_price}
                            onChange={(e) =>
                              updateSpecialEntry(
                                special.product,
                                "special_price",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor={`limit-${special.product}`}>
                            {t(
                              "back.management.menu.dailySpecial.limitPerOrder",
                            )}
                          </Label>
                          <Input
                            id={`limit-${special.product}`}
                            type="number"
                            min="1"
                            value={special.limit_per_order}
                            onChange={(e) =>
                              updateSpecialEntry(
                                special.product,
                                "limit_per_order",
                                parseInt(e.target.value) || 1,
                              )
                            }
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor={`name-${special.product}`}>
                            {t("back.management.menu.dailySpecial.specialName")}
                          </Label>
                          <Input
                            id={`name-${special.product}`}
                            placeholder={t(
                              "back.management.menu.dailySpecial.specialNamePlaceholder",
                            )}
                            value={special.name}
                            onChange={(e) =>
                              updateSpecialEntry(
                                special.product,
                                "name",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>

                      {/* Option Lists - Inline */}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <Label>{t("Option Lists")}</Label>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              toggleExpandOptionLists(special.product)
                            }
                          >
                            {expandedOptionLists.has(special.product) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </div>

                        {/* Selected chips */}
                        {special.option_lists.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {special.option_lists.map((ol) => (
                              <span
                                key={ol.id}
                                className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs font-medium"
                              >
                                {ol.name}
                                {ol.required && (
                                  <span className="text-muted-foreground">
                                    *
                                  </span>
                                )}
                                <button
                                  onClick={() =>
                                    removeOptionList(special.product, ol.id)
                                  }
                                  className="ml-0.5 hover:text-destructive"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Expandable checkbox list */}
                        {expandedOptionLists.has(special.product) && (
                          <div className="max-h-32 space-y-1 overflow-y-auto rounded-md border p-2">
                            {allOptionLists.length === 0 ? (
                              <p className="text-xs text-muted-foreground">
                                {t("No option lists available")}
                              </p>
                            ) : (
                              allOptionLists.map((ol) => (
                                <label
                                  key={ol.id}
                                  className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-muted/50"
                                >
                                  <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5"
                                    checked={special.option_lists.some(
                                      (s) => s.id === ol.id,
                                    )}
                                    onChange={() =>
                                      toggleOptionList(special.product, ol)
                                    }
                                  />
                                  <span>{ol.name}</span>
                                  {ol.required && (
                                    <span className="rounded bg-muted px-1 text-xs text-muted-foreground">
                                      {t("Required")}
                                    </span>
                                  )}
                                </label>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("back.management.menu.dailySpecial.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedSpecials.length === 0 || isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("back.management.menu.dailySpecial.save")} (
            {selectedSpecials.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
